import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch, apiPost } from "@/lib/api";

export interface TrackingPlayerRow {
  player: { id: string; name: string; ign: string; avatar_url?: string | null };
  riot_id: {
    id: string;
    riot_id: string;
    region: string;
    is_primary: boolean;
  } | null;
  tracking: {
    id: string;
    enabled: boolean;
    sync_status: string;
    last_synced_at?: string | null;
    next_sync_at: string;
    latest_match_at?: string | null;
    last_error?: string | null;
  } | null;
}

export interface TrackedMatchRow {
  stats: {
    player_id: string;
    agent: string;
    rank?: string | null;
    won: boolean;
    rounds_won: number;
    rounds_lost: number;
    score: number;
    kills: number;
    deaths: number;
    assists: number;
    headshots: number;
    bodyshots: number;
    legshots: number;
    damage_made: number;
    damage_received: number;
  };
  match: {
    match_id: string;
    map: string;
    started_at: string;
    rounds_played: number;
  };
  player: { id: string; name: string; ign: string };
}

export interface HeatmapEvent {
  id: string;
  player_id: string;
  match_id: string;
  round_number: number;
  event_type: string;
  map: string;
  x: number;
  y: number;
  target_x?: number | null;
  target_y?: number | null;
  weapon?: string | null;
  site?: string | null;
  occurred_at_ms?: number | null;
}

export interface ValorantMatchDetail {
  match: {
    id: string;
    map: string;
    region: string;
    queue: string;
    started_at: string;
    duration_seconds?: number | null;
    rounds_played: number;
    game_version?: string | null;
    season_id?: string | null;
    teams: Record<
      string,
      { has_won?: boolean; rounds_won?: number; rounds_lost?: number }
    >;
  };
  scoreboard: Array<{
    puuid?: string;
    player_id?: string;
    name: string;
    is_tracked: boolean;
    team: string;
    agent: string;
    rank?: string | null;
    kills: number;
    deaths: number;
    assists: number;
    score: number;
    acs: number;
    adr: number;
    headshot_percent: number;
    headshots: number;
    bodyshots: number;
    legshots: number;
    damage_made: number;
    damage_received: number;
    economy_spent: number;
    economy_loadout_value: number;
    ability_casts: { c: number; q: number; e: number; x: number };
  }>;
  rounds: Array<{
    number: number;
    winning_team?: string;
    result?: string;
    planted: boolean;
    defused: boolean;
    tracked_kills: number;
    tracked_deaths: number;
  }>;
  events: HeatmapEvent[];
  insights: Array<{
    player_id: string;
    name: string;
    opening_kills: number;
    opening_deaths: number;
    opening_differential: number;
    kill_death: number;
    damage_differential: number;
    economy_efficiency: number;
  }>;
}

export function useValorantMatchDetail(orgId?: string, matchId?: string) {
  return useQuery({
    queryKey: ["valorant-tracking", orgId, "match", matchId],
    enabled: Boolean(orgId && matchId),
    queryFn: () =>
      apiGet<ValorantMatchDetail>(
        `/organizations/${orgId}/valorant-tracking/matches/${matchId}`,
      ),
  });
}

export function useValorantTracking(
  orgId?: string,
  filters?: {
    playerId?: string;
    rosterId?: string;
    days?: number;
    map?: string;
    eventType?: string;
  },
) {
  const client = useQueryClient();
  const base = `/organizations/${orgId}/valorant-tracking`;
  const query = (values: Record<string, string | number | undefined>) => {
    const params = new URLSearchParams();
    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== "") params.set(key, String(value));
    });
    return params.size ? `?${params}` : "";
  };

  const players = useQuery({
    queryKey: ["valorant-tracking", orgId, "players", filters?.rosterId],
    enabled: Boolean(orgId),
    queryFn: () =>
      apiGet<TrackingPlayerRow[]>(
        `${base}/players${query({ rosterId: filters?.rosterId })}`,
      ),
  });
  const analytics = useQuery({
    queryKey: ["valorant-tracking", orgId, "analytics", filters],
    enabled: Boolean(orgId),
    queryFn: () =>
      apiGet<{ days: number; matches: TrackedMatchRow[] }>(
        `${base}/analytics${query({ playerId: filters?.playerId, rosterId: filters?.rosterId, days: filters?.days })}`,
      ),
  });
  const heatmap = useQuery({
    queryKey: ["valorant-tracking", orgId, "heatmap", filters],
    enabled: Boolean(orgId && filters?.eventType),
    queryFn: () =>
      apiGet<HeatmapEvent[]>(
        `${base}/heatmap${query({ playerId: filters?.playerId, map: filters?.map, eventType: filters?.eventType, days: filters?.days })}`,
      ),
  });
  const invalidate = () =>
    client.invalidateQueries({ queryKey: ["valorant-tracking", orgId] });
  const enable = useMutation({
    mutationFn: ({
      playerId,
      riotIdId,
    }: {
      playerId: string;
      riotIdId: string;
    }) =>
      apiPost(`${base}/players/${playerId}/enable`, {
        riotIdId,
        consent: true,
      }),
    onSuccess: invalidate,
  });
  const disable = useMutation({
    mutationFn: (playerId: string) =>
      apiPatch(`${base}/players/${playerId}/disable`),
    onSuccess: invalidate,
  });
  const sync = useMutation({
    mutationFn: (playerId: string) =>
      apiPost(`${base}/players/${playerId}/sync`),
    onSuccess: invalidate,
  });

  return { players, analytics, heatmap, enable, disable, sync };
}
